import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import debounce from 'lodash.debounce'
import { Check } from '@mui/icons-material'
import AddIcon from '@mui/icons-material/Add'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { OverlayState } from '@/hooks/useOverlay'
import { useGetPDPurchaseOrders } from '@/services/hooks/relay/dynamic'
import { useGetVendorsLiveList } from '@/services/hooks/relay/live-lists'
import { PDPurchaseOrder } from '@/types/po-match'
import { PURCHASE_ORDER_INFO_TYPE_ID } from '@/utils/info-types'
import { Dialog, DialogContent, DialogFooter } from '@/components/dialog'
import LiveListPicker from '@/components/integration-settings/LiveListPicker'
import LineItemsTable from './LineItemsTable'
import { formatValue, getMaxDecimals } from './shared'

type AddPurchaseOrderDialogProps = {
  overlay: OverlayState
  orgId: string
  orgCredentialTypeId: string
  defaultVendorId: string | null
  addedPoIds: string[]
  onAddPurchaseOrder: (purchaseOrder: PDPurchaseOrder) => void
}

// Width of the expand icon button area in the accordion summary (row-reverse layout)
const ICON_SPACER_WIDTH = 33
// Grid template matching PO #, Vendor, Date, Total, Add columns
const PO_COLUMNS_GRID = '160px 1fr 130px 110px 40px'

export default function AddPurchaseOrderDialog({
  overlay,
  orgId,
  orgCredentialTypeId,
  defaultVendorId,
  addedPoIds,
  onAddPurchaseOrder,
}: AddPurchaseOrderDialogProps) {
  const [searchInputValue, setSearchInputValue] = useState('')
  const [poNumberSearch, setPoNumberSearch] = useState('')
  const [vendorSearchValue, setVendorSearchValue] = useState('')
  const [selectedVendorNum, setSelectedVendorNum] = useState('')

  const {
    vendors,
    hasNextPage: hasNextVendorPage,
    fetchNextPage: fetchNextVendorPage,
    isPending: isPendingVendors,
    isFetching: isFetchingVendors,
  } = useGetVendorsLiveList({
    orgId,
    orgCredentialTypeId,
    filters: {
      query: vendorSearchValue,
      fields__only: 'entity_id,pd_display_name,external_vendor_id',
      limit: '100',
    },
    enabled: overlay.isOpen,
    placeholderData: (previousData) => previousData,
  })

  const selectedVendor = useMemo(
    () =>
      vendors.find((vendor) => vendor.external_vendor_id === selectedVendorNum),
    [selectedVendorNum, vendors],
  )

  const selectedVendorName = useMemo(
    () => selectedVendor?.pd_display_name || null,
    [selectedVendor],
  )

  useEffect(() => {
    if (!overlay.isOpen || selectedVendorNum || vendors.length === 0) return

    if (!defaultVendorId) return

    const matchedVendor = vendors.find(
      (vendor) => vendor.entity_id === defaultVendorId,
    )

    if (matchedVendor) {
      setSelectedVendorNum(matchedVendor.external_vendor_id || '')
    }
  }, [defaultVendorId, overlay.isOpen, selectedVendorNum, vendors])

  const {
    pdPurchaseOrders: pdPurchaseOrders,
    isPending,
    isFetching,
    hasNextPage,
    fetchNextPage,
  } = useGetPDPurchaseOrders({
    infoTypeId: PURCHASE_ORDER_INFO_TYPE_ID,
    orgId,
    orgCredentialTypeId,
    filters: {
      ...(poNumberSearch ? { number: poNumberSearch } : {}),
      vendor_num: selectedVendorNum,
      status: 'open',
    },
    enabled: overlay.isOpen && !!selectedVendorNum,
    placeholderData: (previousData) => previousData,
  })

  const vendorNameMap = useMemo(
    () =>
      new Map(
        vendors.map((vendor) => [vendor.entity_id, vendor.pd_display_name]),
      ),
    [vendors],
  )

  const title = selectedVendorName
    ? poNumberSearch
      ? `Results for "${poNumberSearch}" in ${selectedVendorName}`
      : `Open purchase orders for ${selectedVendorName}`
    : 'Search Results'

  let contentMessage: string | null = null
  if (!selectedVendorNum) {
    contentMessage = 'Select a vendor to browse open purchase orders.'
  } else if (isPending) {
    contentMessage = 'Searching purchase orders...'
  } else if (!pdPurchaseOrders.length) {
    contentMessage = 'No open purchase orders found.'
  }

  const resetState = useCallback(() => {
    setSearchInputValue('')
    setPoNumberSearch('')
    setVendorSearchValue('')
    setSelectedVendorNum('')
  }, [])

  const handleClose = useCallback(() => {
    resetState()
    overlay.close()
  }, [overlay, resetState])
  const debouncedSetPoNumberSearch = useMemo(
    () => debounce((value: string) => setPoNumberSearch(value), 400),
    [],
  )

  const debouncedSetPoNumberSearchRef = useRef(debouncedSetPoNumberSearch)
  debouncedSetPoNumberSearchRef.current = debouncedSetPoNumberSearch

  useEffect(() => {
    return () => {
      debouncedSetPoNumberSearchRef.current.cancel()
    }
  }, [])

  return (
    <Dialog
      {...overlay}
      title="Add Purchase Order"
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogContent>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Box sx={{ width: '100%' }}>
            <LiveListPicker
              label="Vendor"
              options={vendors}
              value={selectedVendor}
              searchValue={vendorSearchValue}
              setSearchValue={setVendorSearchValue}
              onChange={(vendor) =>
                setSelectedVendorNum(vendor?.external_vendor_id || '')
              }
              hasNextPage={hasNextVendorPage}
              fetchNextPage={fetchNextVendorPage}
              isLoading={isPendingVendors}
              isFetching={isFetchingVendors}
              disabled={!overlay.isOpen}
            />
          </Box>
          <TextField
            fullWidth
            size="small"
            label="PO Number"
            value={searchInputValue}
            onChange={(e) => {
              const value = e.target.value
              setSearchInputValue(value)
              debouncedSetPoNumberSearch(value.trim())
            }}
            helperText="PO number must match exactly as it appears in your system."
          />
        </Stack>

        <Typography variant="subtitle2" color="text.secondary">
          {title}
        </Typography>

        {contentMessage ? (
          <Typography variant="body2" color="text.secondary">
            {contentMessage}
          </Typography>
        ) : (
          <>
            {/* Header row */}
            <Stack
              direction="row"
              alignItems="center"
              sx={{ px: 2, py: 0.5, mt: 2 }}
            >
              <Box sx={{ width: ICON_SPACER_WIDTH, flexShrink: 0 }} />
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: PO_COLUMNS_GRID,
                  flex: 1,
                  columnGap: 2,
                }}
              >
                {['PO #', 'Vendor', 'Date', 'Total', ''].map((label) => (
                  <Typography
                    key={label}
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
                  >
                    {label}
                  </Typography>
                ))}
              </Box>
            </Stack>

            {pdPurchaseOrders.map((po) => {
              const isAdded = addedPoIds.includes(po.external_id)
              const totalDecimals = Math.max(2, getMaxDecimals([po.amount]))
              return (
                <Accordion
                  key={po.external_id}
                  slotProps={{
                    transition: {
                      mountOnEnter: true, // This ensures the details are only rendered when expanded, improving performance for large lists
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      flexDirection: 'row-reverse',
                      '& .MuiAccordionSummary-expandIconWrapper': {
                        marginRight: 1,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: PO_COLUMNS_GRID,
                        flex: 1,
                        columnGap: 2,
                        alignItems: 'center',
                      }}
                    >
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {po.number}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {vendorNameMap.get(po.vendor_id) || po.vendor_id || '-'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {po.posted_on || '-'}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontFamily: 'monospace' }}
                        noWrap
                      >
                        {formatValue(po.amount, {
                          decimals: totalDecimals,
                          prefix: '$',
                        })}
                      </Typography>
                      <Tooltip title={isAdded ? 'Added' : 'Add purchase order'}>
                        {isAdded ? (
                          <Check
                            color="success"
                            fontSize="small"
                            sx={{ ml: '6px' }}
                          />
                        ) : (
                          <div>
                            <IconButton
                              component="span"
                              color="primary"
                              size="small"
                              role="button"
                              tabIndex={0}
                              aria-label="Add purchase order"
                              onClick={(event) => {
                                event.stopPropagation()
                                onAddPurchaseOrder(po)
                                handleClose()
                              }}
                              onKeyDown={(event) => {
                                if (event.key !== 'Enter' && event.key !== ' ')
                                  return
                                event.preventDefault()
                                event.stopPropagation()
                                onAddPurchaseOrder(po)
                                handleClose()
                              }}
                              onFocus={(event) => event.stopPropagation()}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </div>
                        )}
                      </Tooltip>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <LineItemsTable
                      rows={po.line_items.map((li) => ({
                        rowId: `${po.external_id}|${li.external_id}`,
                        description: li.description,
                        jobNumber: li.job_num,
                        phaseNumber: li.phase_num,
                        quantity: li.quantity,
                        remainingQuantity: li.remaining_quantity,
                        rate: li.unit_price,
                        amount: li.amount,
                        remainingAmount: li.remaining_amount,
                      }))}
                    />
                  </AccordionDetails>
                </Accordion>
              )
            })}
            {hasNextPage && (
              <Button
                variant="text"
                size="small"
                onClick={() => fetchNextPage()}
                disabled={isFetching}
                sx={{ alignSelf: 'center' }}
              >
                {isFetching
                  ? 'Loading more purchase orders...'
                  : 'Load more purchase orders'}
              </Button>
            )}
          </>
        )}
      </DialogContent>
      <DialogFooter>
        <Button variant="text" onClick={handleClose}>
          Cancel
        </Button>
      </DialogFooter>
    </Dialog>
  )
}
